/**
 * useTaxableValueInputs - Business Logic Hook
 *
 * Extracts all calculation and state management logic from TaxableValueInputs component.
 * This hook provides a clean, testable API for managing taxable value inputs.
 *
 * Pattern Benefits:
 * - Testing: Logic can be tested independently from UI
 * - Separation: Component focuses on rendering, hook handles business logic
 * - Reusability: Pattern can be applied to other input builders
 * - Maintainability: Reduces component size and complexity
 *
 * @example
 * const {
 *   handlers: { handleInputChange, markTouched },
 *   computed: { validationErrors, allFieldsTouched },
 *   refs: { initialTaxableValueInputRef },
 *   constants: { MIN_PERCENT, MAX_PERCENT, MIN_CURRENCY }
 * } = useTaxableValueInputs({ values, onUpdate, onBlur, showValidation });
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TaxableValueBuilder } from '~/builders/taxable-value/TaxableValueBuilder';
import type { TaxValueInputs, TaxValueInputsForm } from '~/types';
import { parseFormattedNumber } from '~/utils/numberParsing';

// Constants
const MIN_PERCENT = 0;
const MAX_PERCENT = 100;
const MIN_CURRENCY = 0;

/**
 * Convert form values to engine format for validation
 * Returns null if any required field is empty, invalid, or not greater than 0
 */
const convertFormToEngineForValidation = (
  formValues: TaxValueInputsForm
): TaxValueInputs | null => {
  // Check all fields are present
  if (
    !formValues.initialTaxableValue?.trim() ||
    !formValues.startYearGrowthRate?.trim() ||
    !formValues.fiveYearGrowthRate?.trim() ||
    !formValues.twentyYearGrowthRate?.trim()
  ) {
    return null;
  }

  // Parse and validate numeric values
  // Note: CurrencyInput returns formatted strings, so we need to strip formatting
  const initialTaxableValue = parseFormattedNumber(formValues.initialTaxableValue);
  const startYearGrowthRate = parseFloat(formValues.startYearGrowthRate);
  const fiveYearGrowthRate = parseFloat(formValues.fiveYearGrowthRate);
  const twentyYearGrowthRate = parseFloat(formValues.twentyYearGrowthRate);

  // Check if all values are valid numbers and greater than 0
  if (
    !Number.isFinite(initialTaxableValue) ||
    !Number.isFinite(startYearGrowthRate) ||
    !Number.isFinite(fiveYearGrowthRate) ||
    !Number.isFinite(twentyYearGrowthRate) ||
    initialTaxableValue <= 0 ||
    startYearGrowthRate <= 0 ||
    fiveYearGrowthRate <= 0 ||
    twentyYearGrowthRate <= 0
  ) {
    return null;
  }

  // Convert percentages from form (0-100) to engine (0-1)
  return {
    startingYearTaxableValue: initialTaxableValue,
    startingYearGrowthRate: startYearGrowthRate / 100,
    fiveYearRate: fiveYearGrowthRate / 100,
    twentyYearRate: twentyYearGrowthRate / 100,
  };
};

interface UseTaxableValueInputsOptions {
  values: TaxValueInputsForm;
  onUpdate: (values: TaxValueInputsForm) => void;
  onBlur?: () => void;
  showValidation?: boolean;
}

export const useTaxableValueInputs = ({
  values,
  onUpdate,
  onBlur,
  showValidation = false,
}: UseTaxableValueInputsOptions) => {
  // State
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // Refs
  const initialTaxableValueInputRef = useRef<HTMLInputElement>(null);

  // Handlers
  const markTouched = useCallback(
    (field: string) => {
      setTouched((prev) => new Set(prev).add(field));
      // Call parent onBlur handler when any field is blurred
      onBlur?.();
    },
    [onBlur]
  );

  const handleInputChange = useCallback(
    (field: keyof TaxValueInputsForm, value: string) => {
      onUpdate({
        ...values,
        [field]: value,
      });
    },
    [values, onUpdate]
  );

  // Computed values
  // Check if all fields have been touched
  const allFieldsTouched = useMemo(() => {
    const requiredFields = [
      'initialTaxableValue',
      'startYearGrowthRate',
      'fiveYearGrowthRate',
      'twentyYearGrowthRate',
    ];
    return requiredFields.every((field) => touched.has(field));
  }, [touched]);

  // Validation errors using builder validation (single source of truth)
  const validationErrors = useMemo(() => {
    const engineInputs = convertFormToEngineForValidation(values);

    // If fields are empty or invalid, don't validate yet (wait for user input)
    if (!engineInputs) {
      return [];
    }

    // Use TaxableValueBuilder validation for consistency
    const validation = TaxableValueBuilder.validateInputs(engineInputs);
    const allErrors = validation.errors;

    // Only show validation errors if:
    // 1. showValidation is true (user clicked Continue), OR
    // 2. All fields in the section have been touched
    if (!showValidation && !allFieldsTouched) {
      return [];
    }

    // If showValidation is true, show all errors regardless of touched state
    if (showValidation) {
      return allErrors;
    }

    // Filter errors based on touched fields (only reached if allFieldsTouched is true)
    const filteredErrors: string[] = [];

    for (const error of allErrors) {
      // Map error messages to field names
      if (
        error.includes('Starting year taxable value') ||
        error.includes('Starting taxable value')
      ) {
        if (touched.has('initialTaxableValue')) {
          filteredErrors.push(error);
        }
      } else if (error.includes('Starting year growth rate')) {
        if (touched.has('startYearGrowthRate')) {
          filteredErrors.push(error);
        }
      } else if (error.includes('Five year growth rate')) {
        if (touched.has('fiveYearGrowthRate')) {
          filteredErrors.push(error);
        }
      } else if (error.includes('Twenty year growth rate')) {
        if (touched.has('twentyYearGrowthRate')) {
          filteredErrors.push(error);
        }
      } else {
        // Unknown error - show if any field is touched
        const hasTouchedField = Array.from(touched).length > 0;
        if (hasTouchedField) {
          filteredErrors.push(error);
        }
      }
    }

    return filteredErrors;
  }, [values, touched, showValidation, allFieldsTouched]);

  // Auto-focus on Initial Taxable Value field when component mounts
  // Only focus if document is visible (user is actively viewing the page)
  // This prevents focus jumping when switching browser tabs/windows
  useEffect(() => {
    // Only auto-focus if document is visible (user is on the page)
    if (document.hidden) {
      return;
    }

    const timeout = setTimeout(() => {
      // Double-check visibility before focusing
      if (!document.hidden && initialTaxableValueInputRef.current) {
        initialTaxableValueInputRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, []);

  return {
    // Handlers
    handlers: {
      handleInputChange,
      markTouched,
    },

    // Computed values
    computed: {
      validationErrors,
      allFieldsTouched,
    },

    // Refs
    refs: {
      initialTaxableValueInputRef,
    },

    // Constants
    constants: {
      MIN_PERCENT,
      MAX_PERCENT,
      MIN_CURRENCY,
    },
  };
};
